import { useEffect, useState } from "react";
import { api, type Agent, type AgentLight, type Light, type LightState } from "../api.ts";
import { colors, serif, tokens } from "../theme.ts";
import { GlassCard, Segment } from "../components/glass.tsx";

// tokens.json has no status colours, and none are invented for the rest of the
// app: these three are used for the dot beside each light and nowhere else. The
// state is also written as a word, so colour is never the only signal.
const LIGHT: Record<LightState, string> = { green: "#4E7A48", yellow: "#C49A2C", red: "#B5473B" };
// An agent's light. Grey is the hairline token, not a new colour.
const AGENT_LIGHT: Record<AgentLight, string> = { green: LIGHT.green, yellow: LIGHT.yellow, grey: colors.hair2 };

/** 10 Oct, 03:20, Berlin time. */
const when = (iso: string) =>
  new Date(iso).toLocaleString("en-GB", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: "Europe/Berlin",
  });

/** "just now", "5 minutes ago", "3 hours ago", "2 days ago". */
export function ago(iso: string, now: Date = new Date()): string {
  const s = Math.max(0, (now.getTime() - new Date(iso).getTime()) / 1000);
  const n = (v: number, unit: string) => `${v} ${unit}${v === 1 ? "" : "s"} ago`;
  if (s < 60) return "just now";
  if (s < 3600) return n(Math.floor(s / 60), "minute");
  if (s < 86400) return n(Math.floor(s / 3600), "hour");
  return n(Math.floor(s / 86400), "day");
}

const WHERE: Record<Agent["runs_where"], string> = {
  chat: "in a chat", routine: "as a routine", local: "on the local machine", script: "as a script",
};

type TabKey = "infra" | "agents";
const TABS: { key: TabKey; label: string }[] = [{ key: "infra", label: "Infrastructure" }, { key: "agents", label: "Agents" }];

/**
 * Status, in two tabs. Infrastructure: one light per fact, with the fact
 * written out. The api decides the colour and the sentence; this screen only
 * draws them, so a light fed from outside (Healthchecks.io, later Uptime
 * Kuma) looks exactly like a built-in one and arrives without a rebuild. No
 * overall verdict, no history, no counts.
 *
 * Agents: read-only, one row per registered agent with where it runs, when it
 * last reported and how that went. Green within its cadence, yellow past it,
 * grey when planned or never run. Nothing here runs an agent.
 *
 * Neither is cached: an old green would be worse than an error.
 */
export function Status({ version }: { version: number }) {
  const [tab, setTab] = useState<TabKey>("infra");

  return (
    <div style={{ padding: `12px ${tokens.space.gutter}px 0` }}>
      <div style={{
        marginBottom: 14, fontFamily: serif, fontSize: tokens.type.screenTitle.size,
        lineHeight: tokens.type.screenTitle.lineHeight, color: colors.ink,
      }}>Status</div>

      <Segment options={TABS} value={tab} onChange={setTab} />
      <div style={{ marginTop: 16 }}>
        {tab === "infra" ? <Infrastructure version={version} /> : <Agents version={version} />}
      </div>
    </div>
  );
}

function Infrastructure({ version }: { version: number }) {
  const [lights, setLights] = useState<Light[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.status().then((r) => { if (live) { setLights(r.lights); setError(null); } }).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [version]);

  return (
    <>
      {error && <p style={{ margin: "0 0 12px", fontSize: 14, color: colors.ink }}>{error}</p>}
      {!lights && !error && <p style={{ margin: 0, fontSize: 15, color: colors.ink }}>Opening…</p>}

      {lights && (
        <GlassCard padding="2px 16px">
          {lights.map((l) => (
            <div key={l.key} style={{ display: "grid", gridTemplateColumns: "10px 1fr auto", gap: 12, padding: "14px 0", borderBottom: `1px solid ${colors.hair}` }}>
              <span aria-hidden style={{ width: 10, height: 10, borderRadius: "50%", background: LIGHT[l.state], marginTop: 5 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, color: colors.ink }}>{l.label}</div>
                {l.detail && <div style={{ fontSize: 12.5, lineHeight: 1.4, color: colors.ink2, marginTop: 2, whiteSpace: "pre-line", overflowWrap: "anywhere" }}>{l.detail}</div>}
              </div>
              <div style={{ textAlign: "right", fontSize: 12.5, lineHeight: 1.4, color: colors.ink2, fontVariantNumeric: "tabular-nums" }}>
                <div>{l.state}</div>
                {l.at && <div>{when(l.at)}</div>}
              </div>
            </div>
          ))}
        </GlassCard>
      )}
    </>
  );
}

function Agents({ version }: { version: number }) {
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.agents().then((r) => { if (live) { setAgents(r); setError(null); } }).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [version]);

  return (
    <>
      {error && <p style={{ margin: "0 0 12px", fontSize: 14, color: colors.ink }}>{error}</p>}
      {!agents && !error && <p style={{ margin: 0, fontSize: 15, color: colors.ink }}>Opening…</p>}
      {agents && agents.length === 0 && (
        <GlassCard padding={16}><p style={{ margin: 0, fontSize: 15, color: colors.ink2 }}>No agents registered yet.</p></GlassCard>
      )}

      {agents && agents.length > 0 && (
        <GlassCard padding="2px 16px">
          {agents.map((a) => (
            <div key={a.key} style={{ display: "grid", gridTemplateColumns: "10px 1fr auto", gap: 12, padding: "14px 0", borderBottom: `1px solid ${colors.hair}` }}>
              <span aria-hidden style={{ width: 10, height: 10, borderRadius: "50%", background: AGENT_LIGHT[a.light], marginTop: 5 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, color: a.state === "live" ? colors.ink : colors.ink2 }}>{a.name}</div>
                {a.purpose && <div style={{ fontSize: 12.5, lineHeight: 1.4, color: colors.ink2, marginTop: 2, overflowWrap: "anywhere" }}>{a.purpose}</div>}
                <div style={{ fontSize: 12.5, lineHeight: 1.4, color: colors.ink2, marginTop: 2 }}>
                  {[`runs ${WHERE[a.runs_where]}`, a.state !== "live" ? a.state : null].filter(Boolean).join(" · ")}
                </div>
                {a.last_note && <div style={{ fontSize: 12.5, lineHeight: 1.4, color: colors.ink, marginTop: 4, overflowWrap: "anywhere" }}>{a.last_note}</div>}
              </div>
              <div style={{ textAlign: "right", fontSize: 12.5, lineHeight: 1.4, color: colors.ink2, fontVariantNumeric: "tabular-nums" }}>
                <div>{a.last_run_at ? ago(a.last_run_at) : "not run yet"}</div>
                {a.last_outcome && <div>{a.last_outcome}</div>}
              </div>
            </div>
          ))}
        </GlassCard>
      )}
    </>
  );
}
